import * as React from "react";
import { isEmpty as _isEmpty, isNull as _isNull } from "lodash";
import { IAppointmentScheduleResponse, IPatientDetail, IPrescription } from "@curefit/care-common";
import { default as AppUtils, LOADING_STATE } from "../utils/AppUtils";
import {
    AppConstants,
    AppMessages
} from "../utils/AppConstants";
import { IOngoingConsultation } from "../models/IOngoingConsultation";
import { IPatientVitalResponse } from "../models/IPatientVitalResponse";
import IPatientLifestylePlanWrapper from "../models/patients/IPatientLifestylePlanWrapper";
import { ISavePlanResponse } from "../../../common/models/ISavePlanResponse";
import { ActionResult } from "../../../common/models/IGenericWorkflowActionResult";
import { ApiManager } from "../api/ApiManager";
import { IPdfTemplates } from "../models/PdfTemplatesModels";
import { ORION_IMG_URL } from "../utils/AppConstants";
import { EHRRequestMapper } from "../api/EHRRequestMapper";
import { IConfigBasedPrescription } from "../models/patients/IConfigBasedPrescription";
import ErrorContainer from "../containers/ErrorContainer";
import CommonPrescriptionPdf from "./templates/CommonPrescriptionPdf";
import AppLoader from "./utils/AppLoader";
import IDoctorSpeciality from "../models/IDoctorSpeciality";

export interface EditPrescriptionReviewPdfProps {
    ongoingConsultation: IOngoingConsultation,
    patientVitals: IPatientVitalResponse[],
    staticSpecialities: IDoctorSpeciality[],
    toggleModal: (flag: boolean) => void,
    patientProfile: IPatientDetail,
    savePrescriptionNew: (consultation: IAppointmentScheduleResponse, prescription: IPrescription, patientPlan: IPatientLifestylePlanWrapper, planId: string, isSelf: boolean) => Promise<any>,
    isPdfDataSaved: boolean,
    onPrescriptionSaveFail: (errorText: string, isPdfDataSaved: boolean) => void,
    onPrescriptionSaveSuccess: () => void,
    pdfTemplates: IPdfTemplates
}

export interface EditPrescriptionReviewPdfState {
    pdfGenerationStatus: LOADING_STATE,
    pdfDataSaveStatus: LOADING_STATE, // check for prescription or plan pdf data save
    signatureImagePreSignedUrlMap: { [index: string]: DocSignatureMap }
    error: string,
    errorMessage: string
}

export interface DocSignatureMap {
    fileCompletePath: string,
    url: string
}

export default class EditPrescriptionReviewPdf extends React.Component<EditPrescriptionReviewPdfProps, EditPrescriptionReviewPdfState> {
    constructor(props: EditPrescriptionReviewPdfProps) {
        super(props);
        this.state = {
            pdfGenerationStatus: LOADING_STATE.LOADING,
            pdfDataSaveStatus: props.isPdfDataSaved ? LOADING_STATE.LOADED : LOADING_STATE.NOT_INITIALIZED,
            signatureImagePreSignedUrlMap: null,
            error: null,
            errorMessage: null
        }
    }

    componentDidMount() {
        AppUtils.closeChatContainer()
        if (this.props.ongoingConsultation && this.props.ongoingConsultation.appointmentSchedule && this.props.ongoingConsultation.appointmentSchedule.consultationProduct) {
            let doctorIds: string[] = [];
            doctorIds.push(this.props.ongoingConsultation.appointmentSchedule.doctor.id.toString());
            ApiManager.getAlbusApis().getDoctorSignatureImageDownloadPreSignedUrl(this.props.ongoingConsultation.appointmentSchedule.doctor.id).then((response: any) => {
                let docSignatureMap: { [index: string]: DocSignatureMap } = {};
                docSignatureMap[this.props.ongoingConsultation.appointmentSchedule.doctor.id] = response;
                this.setState({
                    signatureImagePreSignedUrlMap: docSignatureMap
                });
            }).catch((err: any) => {
                throw (err)
            })
        }
    }

    preventModalClose = (event: any) => {
        event.stopPropagation();
    }

    clearErrorMessage = () => {
        this.setState({
            error: null,
            errorMessage: null
        })
    }

    savePrescription = () => {
        let prescription = this.props.ongoingConsultation.medicalProfile &&
            this.props.ongoingConsultation.medicalProfile.currentConsultationData ?
            EHRRequestMapper.getEHRSavePrescriptionRequestFromConfigBasedPrescription(this.props.ongoingConsultation.medicalProfile)
            : {};
        const isSelf = this.props.patientProfile.relationship && this.props.patientProfile.relationship.toUpperCase() === AppConstants.patientRelationships.SELF;
        this.props.savePrescriptionNew(this.props.ongoingConsultation.appointmentSchedule, prescription, null, null, isSelf).then((saveResponse: ISavePlanResponse) => {
            if (saveResponse.status === ActionResult.SUCCESS) {
                // Show Success Modal
                this.props.onPrescriptionSaveSuccess();
                this.setState({ pdfDataSaveStatus: LOADING_STATE.LOADED });
            } else {
                const errorMsg = AppUtils.getWorkflowFailureError(saveResponse.workflowStates);
                this.props.onPrescriptionSaveFail(errorMsg || AppMessages.SAVE_PRESCRIPTION_UNSUCCESSFUL, false);
            }
        }).catch((error) => {
            if (!_isEmpty(error) && !_isEmpty(error.workflowStates)) {
                const errorMsg = AppUtils.getWorkflowFailureError(error.workflowStates);
                this.props.onPrescriptionSaveFail(errorMsg, false);
            } else {
                this.props.onPrescriptionSaveFail(null, false);
            }
        });
    };

    closeModal = () => {
        this.props.toggleModal(false);
    };

    handleSaveClick = () => {
        this.setState({
            pdfDataSaveStatus: LOADING_STATE.LOADING
        }, () => {
            this.savePrescription()
        })
    };

    generatePDF = (isPreview: boolean) => {
        this.setState({
            pdfGenerationStatus: LOADING_STATE.LOADED
        })
    };

    handleTryAgainOnClick = () => {
        if (this.state.pdfGenerationStatus !== LOADING_STATE.LOADING) {
            this.generatePDF(false); // Generate PDF for mail and to show in modal
        }
        this.generatePDF(true); // Generate PDF for printing
    };

    render() {
        const hasOngoingDetails = !_isEmpty(this.props.ongoingConsultation);
        const pdfSaveInProgress = this.state.pdfDataSaveStatus === LOADING_STATE.LOADING;
        let pdfPrescription = null, systemicExaminations = null, examinations = null, entExaminations = null, prescriptionSignatureObj = null, allergies: any = null;
        if (hasOngoingDetails && this.props.ongoingConsultation.medicalProfile) {
            pdfPrescription = { ...this.props.ongoingConsultation.medicalProfile };
            pdfPrescription.prescription = this.props.ongoingConsultation.medicalProfile.currentConsultationData ? { ...this.props.ongoingConsultation.medicalProfile.currentConsultationData } : null;
        }
        if (hasOngoingDetails && this.props.ongoingConsultation.medicalProfile && this.props.ongoingConsultation.medicalProfile.currentConsultationData) {
            systemicExaminations = this.props.ongoingConsultation.medicalProfile.currentConsultationData.systemicExaminations || {};
            examinations = this.props.ongoingConsultation.medicalProfile.currentConsultationData.generalExaminations || {};
            entExaminations = this.props.ongoingConsultation.medicalProfile.currentConsultationData.entExaminations || {};
            allergies = this.props.ongoingConsultation.medicalProfile.currentConsultationData.allergies || {};
        }

        if (AppUtils.isDefined(this.state.signatureImagePreSignedUrlMap)) {
            prescriptionSignatureObj = this.state.signatureImagePreSignedUrlMap[this.props.ongoingConsultation.appointmentSchedule.doctor.id]
        }
        let errorMessage = !_isNull(this.state.error) ? this.state.error : AppMessages.PRESCRIPTION_VALIDATION_ERROR;
        let errorDetails = this.state.errorMessage;
        return (
            <div className={"review-prescription-container"} onClick={this.preventModalClose}>
                {!_isNull(this.state.error) &&
                    <ErrorContainer
                        errorText={errorMessage}
                        errorDetails={errorDetails}
                        errorCode={0}
                        closeError={this.clearErrorMessage}
                        reportError={true} rollbarPayload={{
                            message: errorMessage,
                            errorDetails: errorMessage,
                            request: this.props.ongoingConsultation.medicalProfile.currentConsultationData
                        }} />
                }
                <div className={"cancel-modal"} onClick={this.closeModal}>x</div>
                <div style={{ overflow: "hidden" }}>
                    {/*Prescription PDF template*/}
                    {((pdfPrescription && pdfPrescription.prescription) || (this.props.ongoingConsultation.medicalProfile && this.props.ongoingConsultation.medicalProfile.currentConsultationData)) &&
                        <CommonPrescriptionPdf
                            pdfTemplates={this.props.pdfTemplates}
                            isPhysioConsultation={false}
                            isTreatmentPlan={false}
                            consultationData={{
                                vitals: this.props.patientVitals,
                                examinations: examinations,
                                systemicExaminations: systemicExaminations,
                                entExaminations: entExaminations,
                                allergies: allergies,
                                appointmentSchedule: this.props.ongoingConsultation.appointmentSchedule,
                                patient: this.props.patientProfile,
                                signatureImagePreSignedUrl: prescriptionSignatureObj,
                                staticSystemicExams: [],
                                staticEntExams: [],
                                staticSpecialistData: this.props.staticSpecialities,
                                medicalProfile: this.props.ongoingConsultation.medicalProfile,
                                consultation: pdfPrescription
                            }}
                            hideHeaderFooter={false}
                            renderHandler={() => this.generatePDF(false)}
                        />
                    }
                </div>
                {pdfSaveInProgress &&
                    <div className={"pdf-upload-status"}>
                        <AppLoader text={"Saving Prescription"} />
                    </div>
                }
                {
                    this.state.pdfGenerationStatus !== LOADING_STATE.LOADING &&
                    this.state.pdfDataSaveStatus !== LOADING_STATE.LOADING &&
                    <div className={"action-icons"}>
                        <div className={"pdf-failed animated fadeInRight edit-info"}>
                            <span>
                                <span className="glyphicon glyphicon-info-sign" />
                                {AppMessages.EDIT_PRESCRIPTION_CONFIRM_DIALOG}
                            </span>
                        </div>
                        {
                            this.state.pdfGenerationStatus === LOADING_STATE.NOT_INITIALIZED &&
                            <div className={"pdf-failed animated fadeInRight"}>
                                <span>
                                    <span className="glyphicon glyphicon-info-sign" />
                                    {"PDF Generation failed"}
                                </span>
                            </div>
                        }
                        <div className={"continue-editing"}>
                            <input className={"cf-btn-rounded-bordered animated fadeInRight"}
                                type="button"
                                value="Edit"
                                onClick={() => {
                                    this.props.toggleModal(false)
                                }}
                            />
                        </div>
                        {
                            this.state.pdfGenerationStatus !== LOADING_STATE.NOT_INITIALIZED ?
                                <div className={"send-prescription"}>
                                    <input className={"cf-btn-gradient animated fadeInRight"}
                                        type="button"
                                        value={"Update"}
                                        onClick={() => {
                                            this.handleSaveClick()
                                        }} />
                                </div> :
                                <div className={"generate-prescription"}>
                                    <input className={"animated fadeInRight"}
                                        type="button"
                                        value="Try again"
                                        onClick={this.handleTryAgainOnClick} />
                                </div>
                        }
                    </div>
                }
            </div>
        )
    }
}